import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Image as ImageIcon, Package, Tag, Calendar, Edit } from 'lucide-react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { useToast } from '../hooks/useToast';
import { fetchCatentryDetails } from '../apiHelper';

/**
 * ViewCatentryDetails Component to display details for a specific catentry.
 * Fetches catentry details using catentryId from navigation state.
 */
const ViewCatentryDetails = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const location = useLocation();
    const [user, setUser] = useState(null);
    const [catentryDetails, setCatentryDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    /**
     * Effect to check user authentication and fetch catentry details.
     * Runs on mount and when location.state changes.
     */
    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (!lcUser) {
            showToast("Please log in to view product details.", "info");
            navigate('/login');
            return;
        }

        const parsedUser = JSON.parse(lcUser);
        setUser(parsedUser);

        const catentryId = location.state?.catentryId;
        if (!catentryId) {
            showToast("No product ID provided.", "error");
            navigate(-1);
            return;
        }

        const loadCatentryDetails = async () => {
            setLoading(true);
            setError(null);
            try {
                const payload = {
                    users_id: parsedUser.users_id,
                    logon_id: parsedUser.logon_id,
                    hpwc: parsedUser.hpwc,
                    catentry_id: catentryId
                };
                const response = await fetchCatentryDetails(payload);
                setCatentryDetails(response);
            } catch (err) {
                const errorMessage = err.response?.data?.detail || `Failed to load product details: ${err.message || err}`;
                setError(errorMessage);
                showToast(errorMessage, 'error');
            } finally {
                setLoading(false);
            }
        };

        loadCatentryDetails();
    }, [location.state, showToast, navigate]);

    if (!user) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-8 text-orange-600">
                <p className="text-lg">Redirecting to login...</p>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <p className="text-text-secondary text-lg">Loading product details...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-4">
                <p className="text-gmail-red text-lg mb-4">Error: {error}</p>
                <Button onClick={() => navigate(-1)} variant="secondary">
                    Go Back
                </Button>
            </div>
        );
    }

    if (!catentryDetails) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-8 text-gray-500">
                <p className="text-lg">No product details found.</p>
                <Button onClick={() => navigate(-1)} variant="secondary" className="mt-4">
                    Go Back
                </Button>
            </div>
        );
    }

    const {
        partnumber,
        productname,
        buyable,
        is_active,
        created_on,
        unitofmeasure,
        listprice,
        attachments,
        catgpenrels,
        owner,
        inventory_lots
    } = catentryDetails;

    const currencySymbol = listprice?.currency_rel?.setcnote || '$';
    const listPriceValue = listprice?.listprice || 0;

    return (
        <div className="container mx-auto px-4 py-6">
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-3xl font-bold text-text-primary flex items-center gap-2">
                    <Package size={28} className="text-gmail-blue" />
                    Product Details
                </h1>
                <div className="flex items-center gap-3">
                    <Button
                        onClick={() => navigate('/catalog/edit-item', { state: { catentryId: catentryDetails.catentry_id } })}
                        variant="primary"
                        className="flex items-center gap-2"
                    >
                        <Edit size={18} />
                        Edit Catalog Entry
                    </Button>
                    <Button
                        onClick={() => navigate(-1)}
                        variant="secondary"
                        className="flex items-center gap-2"
                    >
                        Go Back
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Product Info Card */}
                <Card className="lg:col-span-2 p-6">
                    <div className="flex flex-col md:flex-row gap-6">
                        {/* Product Image/Placeholder */}
                        <div className="flex-shrink-0 w-full md:w-48 h-48 bg-gray-100 rounded-lg flex items-center justify-center">
                            {attachments.length > 0 ? (
                                <img
                                    src={`${attachments[0].attachmenturl}`}
                                    alt={productname}
                                    className="w-full h-full object-cover rounded-lg"
                                />
                            ) : (
                                <ImageIcon size={64} className="text-gray-400" />
                            )}
                        </div>

                        {/* Product Basic Info */}
                        <div className="flex-1">
                            <h2 className="text-2xl font-semibold text-text-primary mb-2">{productname}</h2>
                            <p className="text-sm text-text-secondary mb-4">Part Number: {partnumber || 'N/A'}</p>
                            
                            <div className="flex items-center gap-2 mb-4">
                                <Tag size={16} className="text-purple-600" />
                                <span className="font-medium">List Price: {currencySymbol} {listPriceValue.toFixed(2)}</span>
                            </div>

                            <div className="flex items-center gap-2 mb-4">
                                <Package size={16} className="text-gmail-yellow" />
                                <span className="font-medium">Unit of Measure: {unitofmeasure?.name || 'N/A'}</span>
                            </div>

                            <div className="flex items-center gap-2 mb-4">
                                {buyable === 1 ? (
                                    <div className="px-2 py-1 bg-gmail-green text-white rounded-full text-xs font-semibold">
                                        For Sale
                                    </div>
                                ) : (
                                    <div className="px-2 py-1 bg-gmail-red text-white rounded-full text-xs font-semibold">
                                        Internal Use
                                    </div>
                                )}
                                <span className="text-sm text-text-secondary ml-2">
                                    Status: {is_active ? 'Active' : 'Inactive'}
                                </span>
                            </div>

                            {created_on && (
                                <div className="flex items-center gap-2">
                                    <Calendar size={16} className="text-gray-600" />
                                    <span className="text-sm text-text-secondary">
                                        Created: {new Date(created_on).toLocaleDateString()}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </Card>

                {/* Inventory Lots Sidebar */}
                <Card className="p-6">
                    <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
                        <Package size={18} className="text-gmail-yellow" />
                        Inventory Lots
                    </h3>
                    {inventory_lots.length > 0 ? (
                        <div className="space-y-3">
                            {inventory_lots.map((lot, index) => (
                                <div key={index} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                                    <p className="text-sm font-medium text-gray-900">Lot {index + 1}</p>
                                    <p className="text-xs text-gray-500">Quantity: {lot.current_quantity} / {lot.initial_quantity}</p>
                                    <p className="text-xs text-gray-500">Unit Cost: {currencySymbol} {lot.unit_cost?.toFixed(2) || '0.00'}</p>
                                    {lot.receipt_datetime && (
                                        <p className="text-xs text-gray-500">
                                            Received: {new Date(lot.receipt_datetime).toLocaleDateString()}
                                        </p>
                                    )}
                                    {lot.expiration_date && (
                                        <p className="text-xs text-gray-500">
                                            Expires: {new Date(lot.expiration_date).toLocaleDateString()}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-text-secondary text-sm">No inventory lots available.</p>
                    )}
                </Card>

                {/* Categories and Organization Section */}
                {catgpenrels && catgpenrels.length > 0 && (
                    <Card className="mt-6 p-6">
                        <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
                            <Tag size={18} className="text-purple-600" />
                            Categories & Organization
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                            {catgpenrels.map((rel, index) => (
                                <div key={index} className="bg-gray-50 p-3 rounded-lg">
                                    <p className="text-sm font-medium text-gray-900">{rel.category?.identifier || 'N/A'}</p>
                                    <p className="text-xs text-gray-500">Catalog: {rel.catalog?.identifier || 'N/A'}</p>
                                    {owner && (
                                        <p className="text-xs text-gray-500">Organization: {owner.organization?.organization_name || 'N/A'}</p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </Card>
                )}
            </div>
        </div>
    );
};

export default ViewCatentryDetails;